import { prefersReducedMotion } from '@bn-games/render-helpers';
import { findKing, isInCheck } from '../game/attacks.js';
import type { Color, Square } from '../game/types.js';
import type { ViewerRotation } from '../render/geometry.js';
import type { TurnBannerView } from '../render/overlay/TurnBanner.js';
import { animatedSquares, type MoveAnimationFrame, type MoveAnimationPlan } from '../render/PieceAnimator.js';
import { bannerFor } from './banner.js';
import type { BoardInteraction } from './BoardInteraction.js';
import type { Match } from './Match.js';
import type { FrameModel } from './SceneRender.js';

export interface FrameInputs {
  readonly match: Match;
  readonly view: ReturnType<BoardInteraction['view']>;
  readonly rotation: ViewerRotation;
  readonly perspective: Color | null;
  /** Replaces the usual "whose move" banner (online: waiting, opponent away). */
  readonly banner: TurnBannerView | null;
  readonly showValidMoves: boolean;
  readonly moveListShown: boolean;
  readonly animation: { readonly plan: MoveAnimationPlan; readonly frame: MoveAnimationFrame } | null;
  readonly hint: { readonly from: Square; readonly to: Square } | null;
  readonly timeMs: number;
}

/** Assemble one frame's model from the match and the interaction state. */
export function buildFrameModel(i: FrameInputs): FrameModel {
  const state = i.match.state;
  const board = state.position.board;
  const last = state.lastMove;
  // A resigned or drawn game leaves no glowing king behind; a mated one does.
  const showCheck = !state.isOver || state.outcome?.termination === 'checkmate';
  return {
    board,
    rotation: i.rotation,
    marks: {
      lastMove: last === undefined ? null : { from: last.from, to: last.to },
      selected: i.view.selected,
      checkSquare: showCheck && isInCheck(board, state.turn) ? findKing(board, state.turn) : null,
    },
    interaction: {
      hovered: i.view.hovered,
      legalTargets: i.showValidMoves ? i.view.legalTargets : [],
      drag: i.view.drag === null ? null : { ...i.view.drag, code: board[i.view.drag.from] ?? 0 },
      focus: i.view.focus,
      hint: i.hint,
      // Reduced motion: a fixed phase keeps the hint visible without pulsing.
      timeMs: prefersReducedMotion() ? 0 : i.timeMs,
    },
    animation: i.animation === null ? null : { frame: i.animation.frame, skip: animatedSquares(i.animation.plan) },
    banner: i.banner ?? bannerFor(i.match, i.perspective),
    gearHovered: i.view.gearHovered,
    moveListHovered: i.view.moveListHovered,
    moveListShown: i.moveListShown,
    clocks: i.match.clock(),
  };
}
